<template>
  <div class="space-y-6">

    <!-- Edit controls -->
    <div class="flex justify-end">
      <button v-if="!isEditing" @click="startEdit"
        class="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
        Edit Triage Data
      </button>
      <div v-else class="flex gap-2">
        <button @click="cancelEdit"
          class="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button @click="save" :disabled="saving"
          class="text-sm px-3 py-1.5 rounded bg-custom-blue text-white hover:opacity-90 disabled:opacity-50">
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>

    <!-- Vitals -->
    <section v-if="vitals || isEditing">
      <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Vitals</h3>

      <!-- View mode -->
      <div v-if="!isEditing" class="grid grid-cols-3 gap-4">
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Temperature</p>
          <p class="font-semibold text-gray-800">{{ vitals?.temperature ? vitals.temperature + ' °C' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Heart Rate</p>
          <p class="font-semibold text-gray-800">{{ vitals?.heart_rate ? vitals.heart_rate + ' bpm' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Blood Pressure</p>
          <p class="font-semibold text-gray-800">
            {{ (vitals?.blood_pressure_systolic && vitals?.blood_pressure_diastolic)
              ? vitals.blood_pressure_systolic + '/' + vitals.blood_pressure_diastolic + ' mmHg' : '—' }}
          </p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Respiratory Rate</p>
          <p class="font-semibold text-gray-800">{{ vitals?.respiratory_rate ? vitals.respiratory_rate + ' /min' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Oxygen Saturation</p>
          <p class="font-semibold text-gray-800">{{ vitals?.oxygen_saturation ? vitals.oxygen_saturation + '%' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">BMI</p>
          <p class="font-semibold text-gray-800">{{ vitals?.bmi ? vitals.bmi + ' kg/m²' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Weight</p>
          <p class="font-semibold text-gray-800">{{ vitals?.weight ? vitals.weight + ' kg' : '—' }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-3">
          <p class="text-xs text-gray-500">Height</p>
          <p class="font-semibold text-gray-800">{{ vitals?.height ? vitals.height + ' cm' : '—' }}</p>
        </div>
        <p v-if="vitals?.notes" class="col-span-3 text-sm text-gray-600 italic">Notes: {{ vitals.notes }}</p>
      </div>

      <!-- Edit mode -->
      <div v-else class="grid grid-cols-3 gap-3">
        <div>
          <label class="text-xs text-gray-500">Temperature (°C)</label>
          <input v-model="form.vitals.temperature" type="number" step="0.1"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">Heart Rate (bpm)</label>
          <input v-model="form.vitals.heart_rate" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">BP Systolic (mmHg)</label>
          <input v-model="form.vitals.blood_pressure_systolic" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">BP Diastolic (mmHg)</label>
          <input v-model="form.vitals.blood_pressure_diastolic" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">Respiratory Rate (/min)</label>
          <input v-model="form.vitals.respiratory_rate" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">Oxygen Saturation (%)</label>
          <input v-model="form.vitals.oxygen_saturation" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">Weight (kg)</label>
          <input v-model="form.vitals.weight" type="number" step="0.1"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">Height (cm)</label>
          <input v-model="form.vitals.height" type="number"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">BMI</label>
          <input v-model="form.vitals.bmi" type="number" step="0.01" readonly
            class="mt-1 block w-full rounded border-gray-200 bg-gray-100 text-sm cursor-not-allowed" />
        </div>
        <div class="col-span-3">
          <label class="text-xs text-gray-500">Notes</label>
          <textarea v-model="form.vitals.notes" rows="2"
            class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
        </div>
      </div>
    </section>
    <div class="grid grid-cols-2 gap-6 items-start">
      <section v-if="assessment || isEditing">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Triage Assessment</h3>

        <!-- View mode -->
        <div v-if="!isEditing" class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-xs text-gray-500">Reason for Visit</p>
            <p class="text-gray-800">{{ assessment?.visit_reason || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Chief Complaint</p>
            <p class="text-gray-800">{{ assessment?.complaint || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Complaint Duration</p>
            <p class="text-gray-800">{{ assessment?.complaint_duration ? assessment.complaint_duration + ' days' : '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Pain Level</p>
            <p class="text-gray-800">{{ assessment?.pain_level != null ? assessment.pain_level + ' / 10' : '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Risk of Fall</p>
            <p class="text-gray-800">{{ assessment?.risk_of_fall != null ? assessment.risk_of_fall + ' / 10' : '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Is Terminal</p>
            <p class="text-gray-800">{{ assessment?.is_terminal != null ? (assessment.is_terminal ? 'Yes' : 'No') : '—' }}</p>
          </div>
        </div>

        <!-- Edit mode -->
        <div v-else class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-xs text-gray-500">Reason for Visit</label>
            <input v-model="form.assessment.visit_reason" type="text"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
          <div>
            <label class="text-xs text-gray-500">Chief Complaint</label>
            <input v-model="form.assessment.complaint" type="text"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
          <div>
            <label class="text-xs text-gray-500">Complaint Duration (days)</label>
            <input v-model="form.assessment.complaint_duration" type="number" step="0.5"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
          <div>
            <label class="text-xs text-gray-500">Pain Level (1–10)</label>
            <input v-model="form.assessment.pain_level" type="number" min="1" max="10"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
          <div>
            <label class="text-xs text-gray-500">Risk of Fall (1–10)</label>
            <input v-model="form.assessment.risk_of_fall" type="number" min="1" max="10"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
        </div>
      </section>

      <!-- Glasgow Coma Scale (view-only) -->
      <section v-if="gcs">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Glasgow Coma Scale</h3>
        <div class="grid grid-cols-4 gap-4">
          <div class="bg-gray-50 rounded-lg p-3">
            <p class="text-xs text-gray-500">Eye Response</p>
            <p class="font-semibold text-gray-800">{{ gcs.eye_response || '—' }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <p class="text-xs text-gray-500">Motor Response</p>
            <p class="font-semibold text-gray-800">{{ gcs.motor_response || '—' }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-3">
            <p class="text-xs text-gray-500">Verbal Response</p>
            <p class="font-semibold text-gray-800">{{ gcs.verbal_response || '—' }}</p>
          </div>
          <div class="rounded-lg p-3 border" :class="[gcsColorClasses.bg, gcsColorClasses.border]">
            <p class="text-xs" :class="gcsColorClasses.label">GCS Score</p>
            <p class="text-2xl font-bold" :class="gcsColorClasses.value">{{ gcs.score || '—' }}</p>
          </div>
        </div>
      </section>
    </div>
    <!-- Assessment -->

    <div v-if="!vitals && !assessment && !gcs" class="text-sm text-gray-400 py-4">
      No triage data recorded for this visit.
    </div>

  </div>
</template>

<script>
import apiClient from '@/services/api'

export default {
  name: 'TriageStageView',

  props: {
    detail: { type: Object, required: true },
  },

  emits: ['detail-updated'],

  data() {
    return {
      isEditing: false,
      saving:    false,
      form:      { vitals: {}, assessment: {} },
    }
  },

  computed: {
    vitals()     { return this.detail.triage_vitals?.[0] || null },
    assessment() { return this.detail.triage_assesment || null },
    gcs() { return this.detail.triage_glasgow_coma_scale || null },
    gcsColorClasses() {
      const s = parseInt(this.gcs?.score)
      if (isNaN(s))  return { bg: 'bg-gray-50',   border: 'border-gray-200',   label: 'text-gray-500',   value: 'text-gray-700'   }
      if (s <= 8)    return { bg: 'bg-red-50',    border: 'border-red-200',    label: 'text-red-600',    value: 'text-red-700'    }
      if (s <= 13)   return { bg: 'bg-orange-50', border: 'border-orange-200', label: 'text-orange-600', value: 'text-orange-700' }
      if (s <= 14)   return { bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'text-yellow-600', value: 'text-yellow-700' }
      return           { bg: 'bg-green-50',  border: 'border-green-200',  label: 'text-green-600',  value: 'text-green-700'  }
    },
  },

  methods: {
    startEdit() {
      this.form = {
        vitals: {
          temperature:               this.vitals?.temperature               || '',
          heart_rate:                this.vitals?.heart_rate                || '',
          blood_pressure_systolic:   this.vitals?.blood_pressure_systolic   || '',
          blood_pressure_diastolic:  this.vitals?.blood_pressure_diastolic  || '',
          respiratory_rate:          this.vitals?.respiratory_rate          || '',
          oxygen_saturation:         this.vitals?.oxygen_saturation         || '',
          weight:                    this.vitals?.weight                    || '',
          height:                    this.vitals?.height                    || '',
          bmi:                       this.vitals?.bmi                       || '',
          notes:                     this.vitals?.notes                     || '',
        },
        assessment: {
          visit_reason:        this.assessment?.visit_reason        || '',
          complaint:           this.assessment?.complaint           || '',
          complaint_duration:  this.assessment?.complaint_duration  || '',
          pain_level:          this.assessment?.pain_level          || '',
          risk_of_fall:        this.assessment?.risk_of_fall        || '',
        },
      }
      this.isEditing = true
    },

    cancelEdit() {
      this.isEditing = false
    },

    async save() {
      this.saving = true
      try {
        const calls = []
        if (this.vitals?.id) {
          calls.push(apiClient.updateTriageVitals(this.vitals.id, this.form.vitals))
        }
        if (this.assessment?.id) {
          calls.push(apiClient.updateTriageAssessment(this.assessment.id, this.form.assessment))
        }

        const results = await Promise.all(calls)
        const failed = results.find(r => !r.success)
        if (failed) {
          this.$toast.error(failed.message || 'Failed to save triage data')
          return
        }

        this.$emit('detail-updated')
        this.isEditing = false
        this.$toast.success('Triage data updated')
      } catch {
        this.$toast.error('Failed to save triage data')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
